'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Check, Info, Plus } from 'lucide-react';

const pricingTiers = [
  {
    id: 'pricing-standard',
    name: 'Standard Events',
    subtitle: 'Bars, Parties, Karaoke, Bingo, Trivia',
    rate: '$75',
    unit: '/hour',
    retainer: '$100 booking retainer',
    color: 'var(--neon-cyan)',
    popular: false,
    features: [
      'Professional DJ Sound System',
      'Up to 500 song library',
      'Karaoke setup included',
      'Bingo & Trivia hosting',
      'Custom playlist via Spotify',
      'Guest song request link',
      '30-day cancellation policy',
      'Retainer forfeited on cancellation',
      'Vendor add-ons available',
    ],
    cta: 'Book Standard Event',
  },
  {
    id: 'pricing-wedding',
    name: 'Wedding Package',
    subtitle: 'Reception — Ceremony Available as Add-On',
    rate: 'Starting at $200',
    unit: '/hour',
    retainer: '$200 booking retainer',
    color: 'var(--neon-gold)',
    popular: true,
    features: [
      'Reception sound & DJ service',
      'Grand entrance coordination',
      'First dance & special dances',
      'Phonetic name pronunciation guide',
      'Minute-by-minute timeline builder',
      'Full vendor coordination hub',
      'Live event professional display',
      'Spotify playlist integration',
      'Guest song request links',
      '60-day cancellation policy',
      'Retainer forfeited on cancellation',
      'All add-on vendors available',
    ],
    cta: 'Book Wedding Package',
    addon: {
      label: 'Ceremony Coverage',
      price: '+$200',
      description: 'Add DJ ceremony coverage as an optional add-on',
    },
    micAddon: {
      label: 'Wedding Party Microphones',
      price: '+$50',
      description: 'Wireless microphones for the wedding party — up to 4 mics',
    },
    officiantAddon: {
      label: 'Ordained Officiant',
      price: '+$350',
      description: 'Christopher Harris officiates your ceremony — add-on only',
    },
  },
  {
    id: 'pricing-corporate',
    name: 'Corporate & Premium',
    subtitle: 'Corporate Events & Special Productions',
    rate: '$75+',
    unit: '/hour',
    retainer: '$100 booking retainer',
    color: 'var(--neon-purple)',
    popular: false,
    features: [
      'Professional AV presentation',
      'Background & event music',
      'Award ceremony hosting',
      'Branded playlist creation',
      'Custom scripted announcements',
      'Multi-zone sound setup',
      'Production-level lighting',
      'Vendor coordination',
      '30-day cancellation policy',
    ],
    cta: 'Request Corporate Quote',
  },
];

interface AddOn {
  label: string;
  price: string;
  description: string;
}

interface PricingTier {
  id: string;
  name: string;
  subtitle: string;
  rate: string;
  unit: string;
  retainer: string;
  color: string;
  popular: boolean;
  features: string[];
  cta: string;
  addon?: AddOn;
  micAddon?: AddOn;
  officiantAddon?: AddOn;
}

export default function PricingSection() {
  const [showRetainerInfo, setShowRetainerInfo] = useState(false);

  return (
    <section id="pricing" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">Transparent Pricing</p>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 gold-gradient-text">Rates & Packages</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">No hidden fees. Clear rates, automated contracts, and flexible payment plans.</p>
        </div>

        {/* Retainer info banner */}
        <div
          className="mb-8 rounded-xl border border-amber-500/30 bg-amber-500/5 px-6 py-4 flex items-start gap-3 cursor-pointer"
          onClick={() => setShowRetainerInfo(!showRetainerInfo)}
        >
          <Info size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-400">Cancellation & Retainer Policy</p>
            {showRetainerInfo && (
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                All booking retainers are <strong className="text-amber-400">strictly non-refundable upon cancellation</strong>, regardless of reason.
                Wedding events require 60-day advance notice. Bar/standard events require 30-day notice.
                Vendor add-on retainers are calculated automatically based on CRM averages and added to your total.
                A 20% coordination fee is automatically applied to all vendor rates.
              </p>
            )}
            {!showRetainerInfo && (
              <p className="text-xs text-muted-foreground mt-0.5">Click to read full policy — retainers are non-refundable on cancellation.</p>
            )}
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingTiers?.map((tier: PricingTier) => (
            <div
              key={tier?.id}
              className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 ${
                tier?.popular ? 'scale-105' : ''
              }`}
              style={{
                background: 'rgba(18,18,26,0.9)',
                border: `1px solid ${tier?.color}40`,
                boxShadow: tier?.popular ? `0 0 40px ${tier?.color}20, 0 8px 40px rgba(0,0,0,0.4)` : '0 4px 20px rgba(0,0,0,0.3)',
              }}
            >
              {tier?.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold gold-gradient text-black">
                  Most Requested
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-1">{tier?.name}</h3>
                <p className="text-xs font-mono text-muted-foreground">{tier?.subtitle}</p>
              </div>

              <div className="mb-2">
                <span className="text-4xl font-extrabold font-mono" style={{ color: tier?.color }}>{tier?.rate}</span>
                <span className="text-muted-foreground text-sm ml-1">{tier?.unit}</span>
              </div>
              <p className="text-xs font-mono text-muted-foreground mb-6">{tier?.retainer}</p>

              <ul className="flex-1 space-y-3 mb-6">
                {tier?.features?.map((feat) => (
                  <li key={`${tier?.id}-feat-${feat}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="shrink-0 mt-0.5" style={{ color: tier?.color }} />
                    {feat}
                  </li>
                ))}
              </ul>

              {/* DJ Add-On badge for wedding */}
              {tier?.addon && (
                <div
                  className="mb-3 rounded-xl px-4 py-3 flex items-center gap-3 border"
                  style={{
                    background: `${tier?.color}08`,
                    borderColor: `${tier?.color}40`,
                  }}
                >
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                    style={{ background: `${tier?.color}20` }}
                  >
                    <Plus size={14} style={{ color: tier?.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold" style={{ color: tier?.color }}>
                      DJ Add-On: {tier.addon.label}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{tier.addon.description}</p>
                  </div>
                  <span className="text-sm font-extrabold font-mono shrink-0" style={{ color: tier?.color }}>
                    {tier.addon.price}
                  </span>
                </div>
              )}

              {/* Microphones Add-On badge for wedding */}
              {(tier as PricingTier)?.micAddon && (
                <div
                  className="mb-3 rounded-xl px-4 py-3 flex items-center gap-3 border"
                  style={{
                    background: `${tier?.color}08`,
                    borderColor: `${tier?.color}40`,
                  }}
                >
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                    style={{ background: `${tier?.color}20` }}
                  >
                    <Plus size={14} style={{ color: tier?.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold" style={{ color: tier?.color }}>
                      DJ Add-On: {(tier as PricingTier).micAddon!.label}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{(tier as PricingTier).micAddon!.description}</p>
                  </div>
                  <span className="text-sm font-extrabold font-mono shrink-0" style={{ color: tier?.color }}>
                    {(tier as PricingTier).micAddon!.price}
                  </span>
                </div>
              )}

              {/* Officiant Add-On badge for wedding */}
              {(tier as PricingTier)?.officiantAddon && (
                <div
                  className="mb-5 rounded-xl px-4 py-3 flex items-center gap-3 border"
                  style={{
                    background: '#10B98108',
                    borderColor: '#10B98140',
                  }}
                >
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                    style={{ background: '#10B98120' }}
                  >
                    <Plus size={14} style={{ color: '#10B981' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold" style={{ color: '#10B981' }}>
                      Add-On: {(tier as PricingTier).officiantAddon!.label}
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{(tier as PricingTier).officiantAddon!.description}</p>
                  </div>
                  <span className="text-sm font-extrabold font-mono shrink-0" style={{ color: '#10B981' }}>
                    {(tier as PricingTier).officiantAddon!.price}
                  </span>
                </div>
              )}

              <Link
                href="/sign-up-login-screen?role=client"
                className="w-full text-center py-3 rounded-xl text-sm font-bold transition-all duration-200 active:scale-95"
                style={{
                  background: tier?.popular ? 'var(--neon-gold)' : `${tier?.color}15`,
                  border: `1px solid ${tier?.color}60`,
                  color: tier?.popular ? '#000' : tier?.color,
                }}
              >
                {tier?.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Payment methods */}
        <div className="mt-12 text-center">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest mb-4">Accepted Payment Methods</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Venmo', 'PayPal', 'Cash App', 'Zelle', 'Chime', 'Credit / Debit Card']?.map((method) => (
              <span
                key={`payment-${method}`}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                {method}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}